import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box, Collapse, useTheme} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import AppDialog from 'src/components-new/feedback/AppDialog'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import Button from 'src/components-new/primitives/Button'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {getMedicalCenterTypeLabel} from 'src/pages-new/medicalCenters/medicalCentersColumns'

interface MedicalCentersLookupDialogProps {
  onClose: () => void
  onRowClick: (row: Record<string, any>) => void
}

const DEFAULT_PAGINATION: PaginationState = {page: 0, rowsPerPage: 10}

const COLUMNS: ColumnDef<Record<string, any>>[] = [
  {key: 'name', header: 'NOME', render: (row) => <>{row.name ?? '—'}</>},
  {key: 'type', header: 'TIPO', render: (row) => <>{getMedicalCenterTypeLabel(row.type)}</>},
  {key: 'city', header: 'CITTÀ', render: (row) => <>{row.city ?? '—'}</>},
]

/**
 * Same shape as DoctorsLookupDialog.tsx — used by ExportDialogNew's "Centro medico" field
 * so it matches the "Medico" field's search-by-dialog UX instead of an inline SelectNew
 * dropdown. Reuses apiClient.getMedicalCenters/getMedicalCentersCount, same as
 * MedicalCentersListNew's server-side pagination.
 */
export default function MedicalCentersLookupDialog({onClose, onRowClick}: MedicalCentersLookupDialogProps): JSX.Element {
  const theme = useTheme()
  // Opened from inside ExportDialogNew (an AppDialog) — without this it renders behind
  // that dialog's Paper instead of above it, same reasoning as DoctorsLookupDialog.
  const dialogZIndex = theme.zIndex.modal + 2

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [name, setName] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [count, setCount] = useState(0)
  const [status, setStatus] = useState<string>()
  const [pagination, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION)
  // Guards against out-of-order responses: the initial, unfiltered fetch this dialog fires on
  // mount can still be in flight when the user opens "Filtra" and searches — without this, a
  // slower unfiltered response landing after a faster filtered one silently overwrites the
  // filtered result with the full list, making the search appear to do nothing.
  const latestRequestIdRef = useRef(0)

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current
    const paginationFilters = {rowsPerPage: pagination.rowsPerPage, page: pagination.page, order: []}
    const fetchedData = await apiClient.getMedicalCenters(appliedFilters, setStatus, paginationFilters) ?? []
    const fetchedCount = await apiClient.getMedicalCentersCount(appliedFilters, setStatus) ?? 0
    if (requestId !== latestRequestIdRef.current) return
    setData(fetchedData)
    const n = Number(fetchedCount)
    if (!isNaN(n)) setCount(n)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSearch = () => {
    setAppliedFilters(name ? [{property: 'name', type: 'text', value: name}] : [])
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleClear = () => {
    setName('')
    setAppliedFilters([])
    setPagination(prev => ({...prev, page: 0}))
  }

  return (
    <AppDialog
      actions={
        <>
          <Button onClick={handleClear} variant="secondary">Annulla</Button>
          <Button onClick={handleSearch} variant="primary">Cerca</Button>
        </>
      }
      maxWidth="lg"
      onClose={onClose}
      sx={{zIndex: dialogZIndex}}
      open
      title="Centri medici"
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <Button
          onClick={() => setFiltersOpen(prev => !prev)}
          startIcon={<FilterListIcon />}
          sx={{alignSelf: 'flex-start'}}
          variant="secondary"
        >
          {filtersOpen ? 'Nascondi filtri' : 'Filtra'}
        </Button>

        <Collapse in={filtersOpen}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
              gap: 2,
              pb: 1,
            }}
          >
            <TextFieldNew fullWidth label="Nome" onChange={setName} value={name} />
          </Box>
        </Collapse>

        <DataTable<Record<string, any>>
          columns={COLUMNS}
          emptyMessage="Nessun centro medico trovato"
          keyField="_id"
          loading={status === STATUS.PENDING}
          onPaginationChange={setPagination}
          onRowClick={onRowClick}
          pagination={pagination}
          rows={data}
          totalElements={count}
        />
      </Box>
    </AppDialog>
  )
}
